import { runAgent } from './loop';
import { createWsProvider, type AgentProvider } from './provider';
import { proxyUrl } from './config';
import { useCanvasStore } from '../store/canvas-store';
import { useAgentRunStore } from './agent-run-store';
import { serializeForAgent } from './serialize-for-agent';
import { historyStore } from '../history/historyStore';
import type { AgentStep } from './loop';
import type { CanvasState, ChatFrame, ChatMessage } from '../store/types';
import { buildNodeOutline, htmlFrameIds } from './nodeOutline';
import { flashTargetFromStep } from './flashTarget';
import { subtreeHtml } from '../chrome/layerSelect';
import { getRenderHtml, framesById } from '../store/commands';
import { setAgentWorking, clearAgentPresence, stepFrameId } from '../presence/agentPresence';
import { useAuth } from '../auth/useAuth';

/** Long-lived WS provider per chat frame. Reused across turns to skip the
 *  handshake and preserve proxy-side conversation history. Evicted on
 *  error/cancel so the next turn reconnects cleanly. */
const providers = new Map<string, AgentProvider>();

function getProvider(frameId: string): AgentProvider {
  let p = providers.get(frameId);
  if (!p) {
    p = createWsProvider(proxyUrl());
    providers.set(frameId, p);
  }
  return p;
}

function evictProvider(frameId: string): void {
  const p = providers.get(frameId);
  if (p) {
    p.close();
    providers.delete(frameId);
  }
}

/** Start a fresh conversation for a chat frame: wipe the visible transcript and
 *  reset the proxy's per-connection agent memory. Safe before any turn has run
 *  (no live provider → server history is already empty, nothing to reset). */
export function newConversation(frameId: string): void {
  useCanvasStore.getState().dispatch({ t: 'chat.clear', id: frameId });
  providers.get(frameId)?.reset();
}

/** Cancel an in-flight turn for a chat frame (Stop button). */
export function cancelChatTurn(frameId: string): void {
  useAgentRunStore.getState().runs[frameId]?.controller.abort();
}

let msgSeq = 0;
const newMessageId = () => `m${Date.now()}_${msgSeq++}`;

/**
 * Run one chat turn scoped to a chat frame. Streams assistant text into a
 * single message updated in place, logs each tool step, shows live phase in
 * the ephemeral run store, and appends a summary on completion. The whole turn
 * is one transaction so Undo reverts it (text, steps, and canvas mutations).
 */
export async function runChatTurn(chatFrameId: string, prompt: string): Promise<void> {
  const store = useCanvasStore.getState();
  const run = useAgentRunStore.getState();
  const chat = store.doc.frames.find((f) => f.id === chatFrameId);
  if (!chat || chat.kind !== 'chat') return;
  // Guard against double-submit while a turn is live.
  if (run.runs[chatFrameId]) return;

  const controller = new AbortController();
  run.start(chatFrameId, controller);

  const append = (message: ChatMessage) =>
    store.dispatch({ t: 'chat.appendMessage', id: chatFrameId, message });

  // Frame-aware turn: an anchored chat (incl. the Editor Panel) scopes the
  // snapshot to its target and tells the model to edit that frame. A global
  // on-canvas chat instead repositions any frames the agent creates to sit
  // just right of the chat node so output lands where you're looking.
  const anchorFrame = chat.anchorId
    ? store.doc.frames.find((f) => f.id === chat.anchorId)
    : undefined;
  const snapshot = turnSnapshot(store, chat);
  const agentPrompt = anchorFrame
    ? `[You are editing the frame "${anchorFrame.title || anchorFrame.id}" (id: ${anchorFrame.id}). Apply changes to that frame unless I explicitly say otherwise.]\n\n${prompt}`
    : prompt;
  const placeOrigin =
    !chat.anchorId && !chat.panelOwned
      ? { x: chat.x + (chat.w ?? 240) + 40, y: chat.y }
      : undefined;

  // Durable safety net: snapshot the canvas before the agent mutates it.
  try {
    await historyStore.putCheckpoint({
      kind: 'auto-turn',
      label: 'before: ' + prompt.slice(0, 40),
      doc: useCanvasStore.getState().doc,
    });
    await historyStore.pruneCheckpoints(20, 50);
  } catch (e) {
    // history must never block a turn, but surface quota/schema errors.
    console.warn('codeframe: pre-turn checkpoint failed', e);
  }

  // Publish Viv as a live participant for the duration of the turn (the running
  // client carries the agent's awareness). Seed it on the frame in scope.
  setAgentWorking(anchorFrame?.id);

  const authorSub = useAuth.getState().email ?? undefined;

  store.beginTransaction('chat turn');
  try {
    append({ role: 'user', text: prompt, ts: Date.now(), authorSub });
    store.dispatch({ t: 'chat.setDraft', id: chatFrameId, draft: '' });

    // Streaming assistant bubble: created lazily on first token, patched after.
    // Reset after each tool step so post-tool text starts a fresh bubble below.
    let assistantId = newMessageId();
    let assistantStarted = false;
    let assistantBuf = '';
    let lastFlushAt = 0;
    const flushAssistant = (forceFinal = false) => {
      if (!assistantBuf) return;
      const now = Date.now();
      if (!forceFinal && now - lastFlushAt < 120) return;
      if (!assistantStarted) {
        assistantStarted = true;
        append({ id: assistantId, role: 'assistant', text: assistantBuf, ts: now });
      } else {
        store.dispatch({ t: 'chat.patchMessage', id: chatFrameId, messageId: assistantId, text: assistantBuf });
      }
      lastFlushAt = now;
    };

    const steps: AgentStep[] = [];

    try {
      await runAgent({
        provider: getProvider(chatFrameId),
        prompt: agentPrompt,
        snapshot,
        placeOrigin,
        signal: controller.signal,
        onThinking: () => run.setPhase(chatFrameId, 'thinking'),
        onText: (delta) => {
          run.setPhase(chatFrameId, 'streaming');
          assistantBuf += delta;
          flushAssistant();
        },
        onStep: (step) => {
          steps.push(step);
          flushAssistant(true); // commit any pending text before the step line
          append({
            role: 'system',
            text: `→ ${step.name}(${shortInput(step.input)}) ${step.ok ? '✓' : '✗ ' + (step.reason ?? '')}`,
            ts: Date.now(),
          });
          const target = flashTargetFromStep(step);
          if (target) useCanvasStore.getState().setFlashRegion(target);
          // Move Viv's presence onto the frame this step touched.
          if (step.ok) { const fid = stepFrameId(step); if (fid) setAgentWorking(fid); }
          run.setPhase(chatFrameId, 'thinking');
          // Fresh bubble for any text the model emits after this step.
          assistantId = newMessageId();
          assistantStarted = false;
          assistantBuf = '';
        },
        onError: (msg) => append({ role: 'system', text: `error: ${msg}`, ts: Date.now() }),
        followCamera: !chat.anchorId,
      });

      flushAssistant(true);

      if (controller.signal.aborted) {
        append({ role: 'system', text: '■ stopped', ts: Date.now() });
        evictProvider(chatFrameId);
      } else if (steps.length > 0) {
        append({ role: 'system', text: summarize(steps), ts: Date.now() });
      }
    } catch (err) {
      // Transport failure (e.g. proxy down / open rejected) or unexpected throw.
      append({ role: 'system', text: `error: ${err instanceof Error ? err.message : String(err)}`, ts: Date.now() });
      evictProvider(chatFrameId);
    }
  } finally {
    store.endTransaction();
    run.end(chatFrameId);
    clearAgentPresence();
  }
}

function summarize(steps: AgentStep[]): string {
  const ok = steps.filter((s) => s.ok);
  const failed = steps.length - ok.length;
  const counts = new Map<string, number>();
  for (const s of ok) counts.set(s.name, (counts.get(s.name) ?? 0) + 1);
  const parts = [...counts.entries()].map(([name, n]) => (n > 1 ? `${name}×${n}` : name));
  const body = parts.length ? parts.join(' · ') : 'no actions';
  return `✓ done · ${body}${failed ? ` · ${failed} failed` : ''}`;
}

function shortInput(input: Record<string, unknown>): string {
  const entries = Object.entries(input).slice(0, 3);
  return entries.map(([k, v]) => `${k}=${shortVal(v)}`).join(' ');
}
function shortVal(v: unknown): string {
  if (typeof v === 'string') return v.length > 24 ? JSON.stringify(v.slice(0, 24) + '…') : JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.length}]`;
  if (typeof v === 'object' && v !== null) return '{…}';
  return String(v);
}

/** A scope-constraint line for the agent when the user has a layer selected. */
export function selectionScopeLine(state: CanvasState): string {
  const sel = state.layerSelection;
  if (!sel) return '';
  return `selected layer: ${sel.cfNodeId} in ${sel.frameId} — mutate only within this subtree unless asked otherwise`;
}

/** The exact HTML of the selected layer's subtree, for precise agent context.
 *  Empty string when no layer is selected or the frame/node can't be resolved. */
export function layerSubtreeBlock(state: CanvasState): string {
  const sel = state.layerSelection;
  if (!sel) return '';
  const frame = state.doc.frames.find((f) => f.id === sel.frameId);
  if (!frame) return '';
  const html = subtreeHtml(getRenderHtml(frame, framesById(state.doc.frames)), sel.cfNodeId);
  return html ? `selected layer HTML:\n${html}` : '';
}

/** Build a scoped snapshot string for the agent: only anchor frame if set. */
export function scopedSnapshot(state: CanvasState, chat: ChatFrame): string {
  if (!chat.anchorId) return serializeForAgent(state);
  const f = state.doc.frames.find((x) => x.id === chat.anchorId);
  if (!f) return serializeForAgent(state);
  return serializeForAgent({ ...state, doc: { ...state.doc, frames: [f] } });
}

/**
 * Compose the snapshot sent to the agent for a chat turn:
 * - anchored chat → that frame's scoped snapshot + its node outline + any scope line
 * - unanchored chat with a layer selected → full canvas + all-frame outline + scope line
 *   (never the bare scope line alone, which would strip all context)
 * - unanchored, nothing selected → undefined (loop builds base serialization + outline)
 */
export function turnSnapshot(state: CanvasState, chat: ChatFrame): string | undefined {
  const anchorFrame = chat.anchorId ? state.doc.frames.find((f) => f.id === chat.anchorId) : undefined;
  const scopeLine = selectionScopeLine(state);
  if (anchorFrame) {
    return [scopedSnapshot(state, chat), buildNodeOutline(state, [anchorFrame.id]), layerSubtreeBlock(state), scopeLine]
      .filter(Boolean).join('\n');
  }
  if (scopeLine) {
    return [serializeForAgent(state), buildNodeOutline(state, htmlFrameIds(state)), layerSubtreeBlock(state), scopeLine]
      .filter(Boolean).join('\n');
  }
  return undefined;
}
